import type {
  WithContext,
  Organization,
  WebSite,
  WebPage,
  FAQPage,
  ItemList,
  ListItem,
  Question,
  Answer,
  BreadcrumbList,
  Person,
  ContactPoint,
} from "schema-dts";
import {
  localizePath,
  useLocale,
  useTranslation,
  type Locale,
} from "@thyra/landing-i18n";
import { SITE_URL } from "../env";
import { FAQ_IDS } from "./faq";
import { CONTACT_EMAIL, TEAM_MEMBERS } from "../config/team";

const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;

/**
 * Translator + locale for page metadata. Structured data is written in the
 * page's own language (`inLanguage`), pointing at that language's URLs.
 */
export function useSeo() {
  const { t } = useTranslation(["seo", "sections", "company"]);
  return { t, locale: useLocale() };
}

type Seo = ReturnType<typeof useSeo>;

function pageUrl(path: string, locale: Locale): string {
  const localized = localizePath(path, locale);
  return `${SITE_URL}${localized === "/" ? "" : localized}`;
}

/** Organization schema for all pages. */
export function getOrganizationSchema(): WithContext<Organization> {
  const contactPoint: ContactPoint = {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: CONTACT_EMAIL,
  };

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "Thyra AI Corporation",
    url: SITE_URL,
    logo: `${SITE_URL}/logo/light.svg`,
    email: CONTACT_EMAIL,
    sameAs: [
      "https://www.linkedin.com/company/111886268",
      "https://www.instagram.com/thyraai/",
    ],
    contactPoint: [contactPoint],
  };
}

/** WebSite schema for all pages. */
export function getWebSiteSchema({ t, locale }: Seo): WithContext<WebSite> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: "Thyra AI",
    url: SITE_URL,
    description: t("seo:website.description"),
    inLanguage: locale,
    publisher: { "@id": ORG_ID },
  };
}

/** FAQPage schema for the home page (same items as FAQSection). */
export function getFAQPageSchema({ t, locale }: Seo): WithContext<FAQPage> {
  const mainEntity: Question[] = FAQ_IDS.map(
    (id): Question => ({
      "@type": "Question",
      name: t(`sections:faq.items.${id}.q`),
      acceptedAnswer: {
        "@type": "Answer",
        text: t(`sections:faq.items.${id}.a`),
      } as Answer,
    }),
  );

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
    inLanguage: locale,
    url: `${pageUrl("/", locale)}#faq`,
  };
}

/** BreadcrumbList for legal/contact pages. */
export function getBreadcrumbSchema(
  items: { name: string; path: string }[],
): WithContext<BreadcrumbList> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

/** Default schemas for the home page: Organization, WebSite, FAQPage. */
export function getHomePageSchemas(
  seo: Seo,
): WithContext<Organization | WebSite | FAQPage>[] {
  return [
    getOrganizationSchema(),
    getWebSiteSchema(seo),
    getFAQPageSchema(seo),
  ];
}

/** Default schemas for all pages: Organization, WebSite. */
export function getDefaultSchemas(
  seo: Seo,
): WithContext<Organization | WebSite>[] {
  return [getOrganizationSchema(), getWebSiteSchema(seo)];
}

function getPersonSchemas({ t }: Seo): WithContext<Person>[] {
  return TEAM_MEMBERS.map(
    (member): WithContext<Person> => ({
      "@context": "https://schema.org",
      "@type": "Person",
      name: member.name,
      jobTitle: t(`company:team.members.${member.id}.role`),
      description: t(`company:team.members.${member.id}.bio`),
      sameAs: [member.linkedinUrl],
      worksFor: { "@id": ORG_ID },
    }),
  );
}

function getWebPageSchema(
  path: string,
  name: string,
  locale: Locale,
): WithContext<WebPage> {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    url: pageUrl(path, locale),
    inLanguage: locale,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORG_ID },
  };
}

export function getBusinessPageSchemas(
  seo: Seo,
): WithContext<Organization | WebSite | WebPage>[] {
  return [
    ...getDefaultSchemas(seo),
    getWebPageSchema("/business", seo.t("seo:business.name"), seo.locale),
  ];
}

export function getAboutPageSchemas(
  seo: Seo,
): WithContext<Organization | WebSite | WebPage | Person>[] {
  return [
    ...getDefaultSchemas(seo),
    getWebPageSchema("/about", seo.t("seo:about.name"), seo.locale),
    ...getPersonSchemas(seo),
  ];
}

export function getTeamPageSchemas(
  seo: Seo,
): WithContext<Organization | WebSite | WebPage | Person>[] {
  return [
    ...getDefaultSchemas(seo),
    getWebPageSchema("/team", seo.t("seo:team.name"), seo.locale),
    ...getPersonSchemas(seo),
  ];
}

export function getContactPageSchemas(
  seo: Seo,
): WithContext<Organization | WebSite | WebPage>[] {
  return [
    ...getDefaultSchemas(seo),
    getWebPageSchema("/contact", seo.t("seo:contact.name"), seo.locale),
  ];
}

/**
 * ItemList of the ready-to-use agents shown on the home page, for crawlers that run JavaScript (the
 * catalog is fetched in the browser, so the prerendered HTML cannot carry it).
 *
 * URLs pointing to internal app redirect endpoints (such as template deep links) are omitted so
 * crawlers do not index or crawl private app onboarding routes.
 */
export function getAgentsItemListSchema(
  name: string,
  agents: { name: string; description: string | null; url?: string }[],
): WithContext<ItemList> {
  const itemListElement: ListItem[] = agents.map(
    (agent, index): ListItem => ({
      "@type": "ListItem",
      position: index + 1,
      name: agent.name,
      ...(agent.description ? { description: agent.description } : {}),
      ...(agent.url ? { url: agent.url } : {}),
    }),
  );
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    numberOfItems: itemListElement.length,
    itemListElement,
  };
}
